import { hasKeyOfAnyType, hasTypeTag } from "@cartographerio/guard";
import { Tagged, unsafeTag } from "@cartographerio/util";
import format from "date-fns-tz/format";
import utcToZonedTime from "date-fns-tz/utcToZonedTime";
import formatDistance from "date-fns/formatDistance";
import parseISO from "date-fns/parseISO";
import { Timestamp } from "./types.generated";

export type TimeZone = Tagged<"TimeZone">;

// date-fns formatting string
// https://date-fns.org/v2.19.0/docs/format
export type TimestampFormat = Tagged<"TimestampFormat">;

export function unsafeTimeZone(str: string): TimeZone {
  return unsafeTag(str);
}

export function unsafeTimestampFormat(str: string): TimestampFormat {
  return unsafeTag(str);
}

export const defaultTimeZone = unsafeTimeZone("Europe/London");

export const utc = unsafeTimeZone("UTC");

export const ddmmyyyy = unsafeTimestampFormat("dd/MM/yyyy");

export const yyyymmdd = unsafeTimestampFormat("yyyy-MM-dd");

export const hhmm = unsafeTimestampFormat("HH:mm");

export const defaultTimestampFormat = unsafeTimestampFormat(
  `${ddmmyyyy} ${hhmm}`
);

export const iso8601TimestampFormat = unsafeTimestampFormat(
  "yyyy-MM-dd'T'kk:mm:ss.SSSX"
);

const iso8601Regex = /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,9}))?Z/;

function isIso8601(value: unknown): value is string {
  return typeof value === "string" && iso8601Regex.test(value);
}

// We would call this isTimestamp
// but there's already a guard defined in core.generated.ts.
// This function does more checks in the iso8601 field.
export function isValidTimestamp(value: unknown): value is Timestamp {
  return (
    hasTypeTag(value, "Timestamp") &&
    hasKeyOfAnyType(value, "iso8601") &&
    isIso8601(value.iso8601)
  );
}

export function timestampToDate(timestamp: Timestamp): Date {
  return parseISO(timestamp.iso8601);
}

export function dateToTimestamp(date: Date): Timestamp {
  return {
    type: "Timestamp",
    iso8601: date.toISOString(),
  };
}

export function nowTimestamp(): Timestamp {
  return dateToTimestamp(new Date());
}

export function epochTimestamp(epoch: number): Timestamp {
  return dateToTimestamp(new Date(epoch));
}

export function iso8601Timestamp(iso8601: string): Timestamp {
  return {
    type: "Timestamp",
    iso8601,
  };
}

interface FormatTimestampOpts {
  // A locale string, e.g. "Europe/London"
  timeZone?: TimeZone;
  // A date/time format: https://date-fns.org/v2.19.0/docs/format
  format?: TimestampFormat;
}

export function formatTimestamp(
  timestamp: Timestamp,
  opts: FormatTimestampOpts = {}
): string {
  const timeZone = opts.timeZone ?? defaultTimeZone;
  const fmt = opts.format ?? defaultTimestampFormat;
  const zoned = utcToZonedTime(parseISO(timestamp.iso8601), timeZone);
  return format(zoned, fmt, { timeZone });
}

export function formatTimestampIso8601(timestamp: Timestamp): string {
  return formatTimestamp(timestamp, {
    timeZone: utc,
    format: iso8601TimestampFormat,
  });
}

export function formatTimestampAgo(
  timestamp: Timestamp,
  now: Timestamp = nowTimestamp()
): string {
  return formatDistance(timestampToDate(timestamp), timestampToDate(now));
}

export function timestampEpoch(timestamp: Timestamp): number {
  return timestampToDate(timestamp).getTime();
}
